/**
 * "Waiting on Corbin · N days" (10/28) — so the office sees where the ball is
 * on a quote sent up for approval, and for how long.
 *
 * The approver's name comes from the server (the configured owner, else the
 * only Admin); with no single approver it reads "the owner". Never hard-coded.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days since `since` (0 = within the last day). */
export function daysSince(since: string | null | undefined, now: Date = new Date()): number | null {
    if (!since) return null;
    const t = new Date(since).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

/** "Waiting on Corbin · 4 days" / "Waiting on Corbin · since today" / "Waiting on the owner". */
export function waitingOnLine(
    approverName: string | null | undefined,
    since: string | null | undefined,
    now: Date = new Date(),
): string {
    const who = approverName?.trim() || "the owner";
    const d = daysSince(since, now);
    if (d == null) return `Waiting on ${who}`;
    return `Waiting on ${who} · ${d === 0 ? "since today" : d === 1 ? "1 day" : `${d} days`}`;
}

/** The approver's first name (null when it isn't one person), and whether the viewer is the org owner. */
export function useApprover(): { firstName: string | null; isYou: boolean } {
    const q = useQuery({
        queryKey: ["quotes", "approver"],
        queryFn: () => api.get<{ firstName: string | null; isYou?: boolean }>("/api/quotes/approver"),
        staleTime: 30 * 60_000,
        retry: false,
    });
    return { firstName: q.data?.firstName ?? null, isYou: q.data?.isYou === true };
}
